import { queryClient } from './queryClient';

/**
 * Cross-identity state purge.
 *
 * Signing out is a client-side route change (`navigate('/login')`), not a page
 * load, and React Query's cache, the persisted zustand stores and the ad-hoc
 * localStorage entries below all outlive it. Sign in as a different user — in
 * the same browser, or in the same tab — and the previous organization's
 * tickets, customers, canned responses, notifications and tenant selection are
 * still there to be rendered. With `staleTime: 5 * 60 * 1000` on the query
 * client, React Query serves that cached data straight away and does not even
 * refetch for five minutes, which is why the wrong organization's data appears
 * intermittently rather than every time.
 *
 * So every identity change — sign-in, sign-out, and the forced logout the API
 * client performs when a session is rejected — drops all of it first.
 *
 * Device preferences are deliberately NOT cleared: theme, accessibility
 * settings and the remembered login email carry no tenant data and belong to
 * the browser, not to the session.
 */

/**
 * Persisted zustand stores whose contents belong to one tenant/user. The store
 * key carries no identity, so whatever is in it is served to whoever signs in
 * next.
 */
const IDENTITY_SCOPED_STORAGE_KEYS = [
  'centraoneplus-canned-responses', // tenant's canned responses
  'centraoneplus-notifications', // user's notifications + dismissed ids
  'tenant-context-storage', // platform admin's selected tenant (feeds X-Tenant-Id)
  'savedTicketFilters', // filters referencing tenant pipelines/agents
  'savedTicketFiltersMigrated',
  'tickets:selectedPipelineId', // a pipeline id from the previous tenant
];

/**
 * Ad-hoc per-record keys written outside any store. Matched by prefix because
 * the suffix is a record id (e.g. `deletedAttachments:<ticketId>`).
 */
const IDENTITY_SCOPED_KEY_PREFIXES = ['deletedAttachments:'];

function removeIdentityScopedKeys(store: Storage): void {
  IDENTITY_SCOPED_STORAGE_KEYS.forEach((key) => store.removeItem(key));

  // Prefixed keys have to be discovered. Collect first — removing while
  // iterating shifts the remaining indexes.
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key && IDENTITY_SCOPED_KEY_PREFIXES.some((p) => key.startsWith(p))) {
      doomed.push(key);
    }
  }
  doomed.forEach((key) => store.removeItem(key));
}

/**
 * Drop every piece of client-held server data so nothing from the outgoing
 * session can be rendered to the incoming one. Never throws — a purge failure
 * must not block a sign-out.
 */
export function resetIdentityScopedState(): void {
  try {
    // Removes cached data AND cancels in-flight queries, so a response that
    // was already on the wire for the previous session cannot land in the new
    // one's cache.
    queryClient.clear();
  } catch {
    // React Query never throws here in practice; a failed purge must still
    // leave the rest of the sign-out to run.
  }

  try {
    for (const store of [localStorage, sessionStorage]) {
      removeIdentityScopedKeys(store);
    }
  } catch {
    // Storage may be unavailable (private mode / blocked cookies).
  }
}
